import { Column } from '@tanstack/react-table'
import { useState } from 'react'
import { IoMdCloseCircle as CloseIcon } from 'react-icons/io'

import { Button } from '@ui/components/core'
import { PlusIcon } from '@ui/components/icons'
import { KeyValue } from '@ui/interfaces'
import { useClickAway } from '@uidotdev/usehooks'

type AddAttributeColumnsProps = {
  columns: Column<KeyValue>[]
  hideAllColumns: () => void
  showAllColumns: () => void
}

export const AddAttributeColumns = ({ columns, hideAllColumns, showAllColumns }: AddAttributeColumnsProps) => {
  const [columnSearch, setColumnSearch] = useState('')
  const [filteredColumns, setFilteredColumns] = useState(columns.slice(1))
  const [showMenu, setShowMenu] = useState(false)

  const menuRef = useClickAway(() => {
    setShowMenu(false)
  })

  const handleColumnSearch = (search: string) => {
    setColumnSearch(search)
    const filtered = columns.filter((column) => column.id.includes(search))
    setFilteredColumns(filtered)
  }

  const columnArray = columnSearch.length > 0 ? filteredColumns : columns.slice(1)

  return (
    <div className="relative">
      <Button
        variant="tertiary"
        size="small"
        icon={<PlusIcon className="size-4 stroke-gray-900 group-hover:stroke-white" />}
        iconPosition="left"
        onClick={() => setShowMenu(!showMenu)}>
        Add columns
      </Button>
      {showMenu && (
        <div
          ref={menuRef as React.RefObject<HTMLDivElement>}
          className="absolute right-0 top-10 z-20 ml-1 w-80 max-w-sm space-y-2 rounded-lg border border-gray-200 bg-white px-4 shadow-lg ring-1 ring-black/5 sm:px-0 lg:max-w-3xl">
          <div className="px-2 pt-2">
            <input
              type="text"
              className="w-full rounded-md border border-gray-300 p-3 px-4 pr-10 text-gray-900 placeholder:text-gray-500 focus:ring-0 sm:text-sm"
              placeholder="Search attributes"
              value={columnSearch}
              onChange={(e) => handleColumnSearch(e.target.value)}
            />
            <CloseIcon
              onClick={() => handleColumnSearch('')}
              className="absolute right-5 top-5 h-6 w-6 cursor-pointer text-gray-500 hover:text-gray-900"
            />
          </div>
          <div className="flex h-64 max-h-full flex-col overflow-y-scroll">
            <div className="relative">
              {columnArray.map((column: Column<KeyValue>) => (
                <div
                  key={column.id}
                  className="flex items-center px-4 py-1 transition duration-150 ease-in-out hover:bg-gray-50 focus:outline-none">
                  <label className="w-full text-sm text-gray-900">
                    <input
                      {...{
                        type: 'checkbox',
                        checked: column.getIsVisible(),
                        onChange: column.getToggleVisibilityHandler(),
                      }}
                      className="mr-2 h-4 w-4 text-primary-500"
                    />
                    {column.id}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex w-full justify-between space-x-2 p-2">
            <button
              className="w-full rounded border border-primary-500 bg-white px-4 py-2 text-sm font-medium text-primary-500 transition duration-150 ease-in-out hover:opacity-70 active:bg-primary-500 active:text-white"
              onClick={hideAllColumns}>
              Deselect All
            </button>
            <button
              className="w-full rounded border border-primary-500 bg-primary-500 px-4 py-2 text-sm font-medium text-white transition duration-150 ease-in-out hover:opacity-70 active:bg-white active:text-primary-500"
              onClick={showAllColumns}>
              Select All
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
